import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import GitInfo from "react-git-info/macro";
import packageJSON from "../package.json";
import { useLocation, useSearchParams } from "react-router-dom";

import i18n from "./i18next";

import { useSession } from "./hooks/useSession";
import { useGetMapConfigQuery } from "./redux/services/vectormapsAPI";
import { useLazyGetFlightFullDetailsQuery } from "./redux/services/flightConnectorAPI";
import { useAppDispatch, useAppSelector } from "./redux/hooks";
import {
  setDefaultLanguage,
  setLanguage,
  setProject,
} from "./redux/slices/applicationSlice";
import { LanguageConfig } from "./redux/services/types";
import useQueryParamConfig from "./hooks/useQueryParamConfig";
import { useDeviceInfo } from "./hooks/useDeviceInfo";
import useMapOptionsConfig from "./hooks/useMapOptionsConfig";

import { parseLanguageObject } from "./utils/parseLanguageObject";
import { decodePlugin, handlePluginResponse } from "./utils/pluginSelect";
import { QueryParam } from "./utils/types";

import MapContainer from "./containers/MapContainer/MapContainer";

import { LoadingMapView } from "./views";
import { replace } from "./utils/navigate";
import PageNotFoundView from "./views/404/404";

const { commit, branch } = GitInfo();

export default function App() {
  const [appIsInitialized, setAppIsInitialized] = useState(false);
  const [searchParams] = useSearchParams();
  const location = useLocation();

  useDeviceInfo();
  const { createSession } = useSession();

  const dispatch = useAppDispatch();

  const { language, project, sessionId, deviceInformation, queryParamsConfig } =
    useAppSelector((state) => state.application);

  const { data: mapData, isLoading, isError, error } = useGetMapConfigQuery();

  useMapOptionsConfig();
  useQueryParamConfig({
    defaultFloor: mapData?.floors.find((floor) => floor.default),
  });

  const [getFlightDetails, flightDetails] = useLazyGetFlightFullDetailsQuery();
  const pluginParam = queryParamsConfig["plugin"];

  useEffect(() => {
    if (pluginParam) {
      const pluginRequest = decodePlugin(pluginParam);
      getFlightDetails(pluginRequest);
    }
  }, [pluginParam, getFlightDetails]);

  useEffect(() => {
    handlePluginResponse(flightDetails.data, dispatch);
  }, [flightDetails.isSuccess, flightDetails.data, dispatch]);

  useEffect(() => {
    if (project) createSession(project);
  }, [createSession, project]);

  const [showSplashScreen, setShowSplashScreen] = useState(true);

  const getAppLanguage = useCallback(
    (defaultLanguage: LanguageConfig, availableLanguages: LanguageConfig[]) => {
      const langQueryParam = searchParams.get(QueryParam.LANG);

      // 1. Try query param
      let language =
        langQueryParam &&
        availableLanguages.find(({ id }) => id === langQueryParam);

      // 2. Try browser language if query param not valid
      if (!language) {
        const browserLang = navigator.languages.find((lang) =>
          availableLanguages.some(({ id }) => id === lang),
        );
        language = availableLanguages.find(({ id }) => id === browserLang);
      }

      // 3. Fallback to default language if no valid language found
      return language ?? defaultLanguage;
    },
    [searchParams],
  );

  // This useEffect is used to log the version, branch and commit hash of the app on load.
  useEffect(() => {
    console.log(
      `Version: ${packageJSON.version}\nBranch: ${branch}\nCommit hash: ${commit.hash}`,
    );
  }, []);

  // Show loading splash screen for minimum of 1 second
  useEffect(() => {
    if (!showSplashScreen) return;

    setTimeout(() => {
      setShowSplashScreen(false);
    }, 1000);
  }, [showSplashScreen]);

  useEffect(() => {
    if (mapData && !appIsInitialized) {
      const { id: language, orientation } = getAppLanguage(
        mapData.languages.default,
        mapData.languages.available,
      );

      dispatch(setDefaultLanguage(mapData.languages.default.id));
      dispatch(setProject(mapData.id));
      dispatch(setLanguage(language));
      document.documentElement.setAttribute("dir", orientation);
      i18n.changeLanguage(language);

      dispatch(
        replace({
          pathOrLocation: location.pathname,
          newQueryParams: { lang: language },
        }),
      );

      setAppIsInitialized(true);
    }
  }, [appIsInitialized, dispatch, getAppLanguage, location.pathname, mapData]);

  useEffect(() => {
    if (!deviceInformation.os || !deviceInformation.osVersion) return;
    if (
      (deviceInformation.os === "iOS" &&
        parseFloat(deviceInformation.osVersion) <= 13) ||
      (deviceInformation.os === "Android" &&
        parseFloat(deviceInformation.osVersion) <= 9)
    ) {
      alert(
        "You are using an unsupported OS version and may experience issues. Please update your OS for optimal performance.",
      );
    }
  }, [deviceInformation.os, deviceInformation.osVersion]);

  // Handle project/map not found error by showing a 404 page
  if (
    isError &&
    error &&
    typeof error === "object" &&
    "status" in error &&
    (error as { status?: number }).status === 404
  ) {
    return <PageNotFoundView />;
  }

  if (isLoading || !mapData || showSplashScreen || !project || !sessionId)
    return <LoadingMapView />;

  return (
    <>
      <Helmet>
        <title>
          {parseLanguageObject(mapData.title, language) || "Living Map"}
        </title>
        <link rel="icon" href={mapData.favicon_url} />
      </Helmet>
      {/* Include a hidden header element to provide structure for screen readers */}
      <h1 className="visuallyHidden">
        {parseLanguageObject(mapData.title, language) || "Living Map"}
      </h1>
      <MapContainer />
    </>
  );
}
